import "./DatePickerWithSlotsPopover.scss";

import { DatePickerWithSlotsMonth } from "./DatePickerMonth";
import React, { useState } from "react";
import { Radio } from "react-bootstrap";
import { DatePickerPopover, DeliveryDateInfo } from "./DatePickerPopover";
import type { PickupDatePickerPopoverProps } from "./DatePickerPopover";
import { FormattedMessage } from "react-intl";
import { ValidDeliveryDate, isPickupDate, type PickupTimeSlot } from "../../server/StoreInfoRequests";
import type { Timeslot } from "../../model/CheckoutData";
import type { PickupDates, DeliveryDates } from "../../stores/DatesProvider";
import { DeliveryDateSuggestions } from "./DeliveryDateSuggestions";
import { DeliveryTimeSlots } from "../common/DeliveryTimeSlots";
import { calculateLastDay, isValidDay } from "./DatePickerUtil";
import { FsdCustomerDatePicker } from "./FsdCustomerDatePicker";
import { formatLocalDate, formatLocalTime, parseDate } from "../../utils/DateUtil";
import { shouldUsePickupTimeslots } from "../../utils/featureFlagUtil";

type OnSelection = (date: Date, timeslot: Timeslot | null | undefined) => void;

export function TimeslotRow({
	date,
	timeslot,
	isSelected,
	onSelection,
}: Readonly<{
	date: Date;
	timeslot: PickupTimeSlot;
	isSelected: boolean;
	onSelection: OnSelection;
}>) {
	const scrollToTopIfMobile = () => {
		if (document.body.scrollWidth < 1024) {
			window.scrollTo({
				top: 0,
				left: 0,
				behavior: "smooth",
			});
		}
	};

	const disabled = timeslot.availableCapacity <= 0;
	let labelCssClass = "unselected";
	if (isSelected) {
		labelCssClass = "selected";
	} else if (disabled) {
		labelCssClass = "disabled";
	}
	return (
		<div className="mfcss_slotpicker_row">
			<Radio
				checked={isSelected}
				name="pickup-timeslot-radio"
				onChange={() => {
					onSelection(date, {
						fromUtc: timeslot.fromDateTimeUTC,
						untilUtc: timeslot.untilDateTimeUTC,
					});
					scrollToTopIfMobile();
				}}
				disabled={disabled}
			>
				<span className={`mfcss_datepicker_slot_label ${labelCssClass}`}>
					{formatLocalTime(timeslot.fromDateTimeUTC)} - {formatLocalTime(timeslot.untilDateTimeUTC)}
				</span>
			</Radio>
		</div>
	);
}
export const PickupTimeSlots = ({
	date,
	selectedTimeslot,
	timeslots,
	onSelection,
}: {
	date: Date;
	selectedTimeslot: Timeslot | null | undefined;
	timeslots: PickupTimeSlot[];
	onSelection: OnSelection;
}) => {
	return (
		<>
			<div
				className="mfcss_slots_header"
				style={{
					position: "relative",
				}}
			>
				<div className="mfcss_datepicker_message">
					<FormattedMessage id={"ORC_TIME_SLOT_PICKER_TITLE"} />
				</div>
			</div>

			<div className="mfcss_slotpicker">
				{timeslots.map((timeslot) => {
					const isSelected =
						(selectedTimeslot &&
							selectedTimeslot.fromUtc === timeslot.fromDateTimeUTC &&
							selectedTimeslot.untilUtc === timeslot.untilDateTimeUTC) ||
						false;
					return (
						<TimeslotRow
							key={timeslot.fromDateTimeUTC}
							date={date}
							timeslot={timeslot}
							onSelection={onSelection}
							isSelected={isSelected}
						/>
					);
				})}
			</div>
		</>
	);
};
export const PickupDateInfo = ({
	dates,
	selectedDate,
	selectedTimeslot,
	dateFormat,
	onSelection,
}: {
	dates: PickupDates | DeliveryDates;
	selectedDate: Date;
	selectedTimeslot: Timeslot | null | undefined;
	dateFormat: string;
	onSelection: OnSelection;
}) => {
	const dateInfo = dates.get(formatLocalDate(selectedDate, dateFormat));
	if (!dateInfo) return null;

	const getMessage = () => {
		if (dateInfo.type === "ValidDate") {
			if (isPickupDate(dateInfo) && dateInfo.pickupTimeSlots.length) {
				return (
					<PickupTimeSlots
						date={selectedDate}
						selectedTimeslot={selectedTimeslot}
						timeslots={dateInfo.pickupTimeSlots}
						onSelection={onSelection}
					/>
				);
			}

			if (dateInfo.isCustomerSlot && (dateInfo as ValidDeliveryDate).deliveryTimes?.length) {
				return (
					<DeliveryTimeSlots
						date={selectedDate}
						timeSlots={(dateInfo as ValidDeliveryDate).deliveryTimes}
						iconClass="icon-clock"
					/>
				);
			}

			if ((dateInfo as ValidDeliveryDate).isAlternative) {
				return <FormattedMessage id="ORC_DELIVERY_DATE_ALTERNATIVE_FOR_STORE_HOLIDAY_NOTIFICATION" />;
			}
		}

		return <FormattedMessage id="ORC_TIME_SLOT_DEFAULT_TEXT" />;
	};

	return (
		<div className="mfcss_datepicker_timeslot_border_left text-overflow-ellipsis mfcss_flexbox-post">
			{getMessage()}
		</div>
	);
};
export const DatePickerWithSlotsPopover = ({
	dates,
	onClose,
	selectedTimeslot,
	selectedDate,
	onSelectDate,
	dateFormat,
	isPickup,
	earliestItemDates,
	localDateFormat,
	showSuggestions,
	maxValidDatesForFsdPicker,
}: PickupDatePickerPopoverProps & {
	earliestItemDates: Set<string> | null | undefined;
	isPickup: boolean;
	localDateFormat: string;
	maxValidDatesForFsdPicker: number;
	selectedTimeslot: Timeslot | null | undefined;
	showSuggestions: boolean;
}) => {
	const [pickerDate, setPickerDate] = useState(selectedDate);

	const selectedDeliveryDateInfo = () => {
		return selectedDate ? (
			<DeliveryDateInfo dates={dates as DeliveryDates} selectedDate={selectedDate} dateFormat={dateFormat} />
		) : null;
	};

	const selectedDateInfo = () =>
		shouldUsePickupTimeslots() && pickerDate ? (
			<PickupDateInfo
				dates={dates}
				selectedDate={pickerDate}
				selectedTimeslot={selectedTimeslot}
				dateFormat={dateFormat}
				onSelection={onSelectDate}
			/>
		) : (
			selectedDeliveryDateInfo()
		);

	const suggestions = () => (
		<DeliveryDateSuggestions
			dates={dates as DeliveryDates}
			selectedDate={selectedDate}
			onSelectDate={onSelectDate}
			earliestItemDates={earliestItemDates || new Set()}
			localDateFormat={localDateFormat}
		/>
	);

	const disableDayByTimeSlot = isPickup && shouldUsePickupTimeslots();
	const validDates = isPickup
		? []
		: [...dates.keys()].filter((dateString) =>
				isValidDay(
					parseDate(dateString),
					dates.get(dateString),
					isPickup,
					disableDayByTimeSlot,
					calculateLastDay(dateFormat),
				),
			);

	if (!isPickup && validDates.length <= maxValidDatesForFsdPicker) {
		return (
			<FsdCustomerDatePicker
				dates={dates}
				selectedDate={selectedDate}
				onSelect={onSelectDate}
				validDates={validDates}
				dateFormat={localDateFormat}
				onClose={onClose}
			/>
		);
	}

	const renderMonth = (year, month) => (
		<DatePickerWithSlotsMonth
			dates={dates}
			year={year}
			month={month}
			selectedDate={pickerDate}
			onSelectDate={isPickup && shouldUsePickupTimeslots() ? setPickerDate : onSelectDate}
			dateFormat={dateFormat}
			disableDayByTimeSlot={disableDayByTimeSlot}
			isPickup={isPickup}
		/>
	);

	return (
		<DatePickerPopover
			dateStrings={[...dates.keys()]}
			onClose={onClose}
			renderMonth={renderMonth}
			renderSlots={selectedDateInfo}
			renderBefore={(showSuggestions && suggestions) || undefined}
		/>
	);
};
